import {createAsyncThunk} from '@reduxjs/toolkit';

import {TDDFilterExpressionDto} from 'features/predicate-expression-form-list';
import {ILookupEntry} from 'shared/types/lookups';
import {TAppState} from 'store/config/types';

import {IReportTableColumn} from '../dd-lookups';
import {fetchTableReportColumns, fetchTableReportFilterConditions} from '../dd-lookups/dd-lookups-api';
import {selectReportTableColumnsByTemplateCode, selectTableReportConfigurationFilterConditions} from '../dd-lookups/dd-lookups-selectors';
import {REPORT_CONFIGURATION_SLICE} from '../report-configuration-constants';
import {
    IDeleteDrillDownFilterByIdArgs,
    IFetchDrillDownFilterByIdArgs,
    IPostDrillDownFilterByIdArgs,
    deleteDrillDownFilterById,
    fetchDrillDownFilterById,
    postDrillDownFilterById,
} from './dd-filters-configuration-api';

export interface ILoadDrilldownFilterData {
    ddFilterExpressionDto: TDDFilterExpressionDto | undefined;
    tableReportFilterLookups: ILookupEntry[];
    childReportColumns: IReportTableColumn[] | undefined;
}

export interface ILoadDrilldownFilterArgs extends IFetchDrillDownFilterByIdArgs {
    childReportTemplateCode: string | null | undefined;
}

export const loadDrilldownFilter = createAsyncThunk<ILoadDrilldownFilterData, ILoadDrilldownFilterArgs>(
    `${REPORT_CONFIGURATION_SLICE}/loadDrilldownFilter`, async (args, {rejectWithValue, getState}) => {
        try {
            const {childReportTemplateCode} = args;

            const tableReportFiltersInStore = selectTableReportConfigurationFilterConditions(getState() as TAppState);
            const childReportColumnsInStore = !childReportTemplateCode ? undefined
                : selectReportTableColumnsByTemplateCode(
                    getState() as TAppState, childReportTemplateCode,
                );

            const fetchDrilldownFilter = async () => {
                try {
                    const fetchResult = await fetchDrillDownFilterById(args);
                    return fetchResult;
                } catch (e) {
                    if (e.response.status === 404) return {data: undefined};
                    throw new Error(e);
                }
            };

            const loadRequests = await Promise.all([
                await fetchDrilldownFilter(),
                await (tableReportFiltersInStore ? Promise.resolve(
                    {data: tableReportFiltersInStore},
                ) : fetchTableReportFilterConditions()),
                await (childReportColumnsInStore ? Promise.resolve({data: childReportColumnsInStore})
                    : childReportTemplateCode ? fetchTableReportColumns({
                        templateCode: childReportTemplateCode,
                    }) : Promise.resolve({data: undefined as IReportTableColumn[] | undefined})),
            ]);

            const [
                ddFilterExpressionDtoResponse,
                tableReportFiltersResponse,
                childReportColumnsResponse,
            ] = loadRequests;

            return {
                ddFilterExpressionDto: ddFilterExpressionDtoResponse.data,
                tableReportFilterLookups: tableReportFiltersResponse.data,
                childReportColumns: childReportColumnsResponse.data,
            };
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateDrilldownFilter = createAsyncThunk<string, IPostDrillDownFilterByIdArgs>(
    `${REPORT_CONFIGURATION_SLICE}/updateDrilldownFilter`, async (args, {rejectWithValue}) => {
        try {
            const {data} = await postDrillDownFilterById(args);
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const deleteDrilldownFilter = createAsyncThunk<string, IDeleteDrillDownFilterByIdArgs>(
    `${REPORT_CONFIGURATION_SLICE}/deleteDrilldownFilter`, async (args, {rejectWithValue}) => {
        try {
            const {data} = await deleteDrillDownFilterById(args);
            return data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
